import axios from 'axios';
import { format } from 'date-fns';

import { ApiResponse } from '../../models/api.response.model';
import { ErrorResponse } from '../../models/error.model';
import { MessageResponse } from '../../models/message.model';
import { Company } from '../../models/company.model';
import { Consignment, ConsignmentResponse, ConsignmentsResponse } from '../../models/consignment.model';
import { authToken } from '../../scripts/authentication.script';
import { Invoice } from '../../models/invoice.model';

export class ApiDigitalAdminConsignment {
  static apiResponse: ApiResponse

  public static async getConsignments(
    id_company: string,
    contact: string,
    status: string,
    sort_field: string,
    sort_mode: string,
    page: number,
    limit: number
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let consignmentsResponse: ConsignmentsResponse | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/consignments.data.json').then(response => { consignmentsResponse = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (consignmentsResponse!.success && consignmentsResponse!.data.items.every(item => item.id_company === id_company)) {
        consignmentsResponse!.data.pagination = { page: page, limit: limit, total: consignmentsResponse!.data.items.length, pages: Math.ceil(consignmentsResponse!.data.items.length / limit) || 1 }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: consignmentsResponse!.success === true ? consignmentsResponse!.data : null
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null 
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/consignments?id_contact=${contact}&status=${status}&sort_field=${sort_field}&sort_mode=${sort_mode}&page=${page}&limit=${limit}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getConsignment(
    id_company: string,
    id_consignment: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let consignmentResponse: ConsignmentResponse | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/consignment.data.json').then(response => { consignmentResponse = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (consignmentResponse!.data.id === id_consignment) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: consignmentResponse!.success === true ? consignmentResponse!.data : null
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null 
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/consignments/${id_consignment}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getConsignmentReport(
    id_company: string,
    id_consignment: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let consignments: Consignment[] | null = null
      let messageResponse: MessageResponse | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/consignments.data.json').then(response => { consignments = response.data.data.items }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { messageResponse = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (consignments!.some(item => id_consignment.includes(item.id))) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: messageResponse!.success === true ? messageResponse!.data : null
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null 
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/consignments/${id_consignment}/report`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postConsignment(
    id_company: string,
    provider: any,
    commission_type: string,
    commission_rate: number,
    start_date: string,
    end_date: string,
    notes: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let consignmentResponse: Consignment | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        consignmentResponse = {
          id: `consignment${(Math.floor(Math.random() * 1000)).toString()}`,
          id_company: id_company,
          id_contact: `contact${(Math.floor(Math.random() * 1000)).toString()}`,
          commission_type: commission_type,
          commission_rate: commission_rate,
          status: 'active',
          start_date: start_date,
          end_date: end_date,
          notes: notes,
          created_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
          updated_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`
        }

        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: consignmentResponse
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/consignments`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        provider: provider,
        commission_type: commission_type,
        commission_rate: commission_rate,
        start_date: start_date,
        end_date: end_date,
        notes: notes
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postConsignmentReceive(
    id_company: string,
    id_consignment: string,
    id_storage_center: string,
    lines: { id_variant: string, product_name: string, unit_price: number, quantity: number }[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let consignmentResponse: Consignment | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        consignmentResponse = {
          id: id_consignment,
          id_company: id_company,
          id_contact: `contact${(Math.floor(Math.random() * 1000)).toString()}`,
          commission_type: 'percentage',
          commission_rate: 0,
          status: 'active',
          start_date: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
          end_date: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
          created_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
          updated_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`
        }

        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: consignmentResponse
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/consignments/${id_consignment}/receive`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        id_storage_center: id_storage_center,
        lines: lines
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postConsignmentReturn(
    id_company: string,
    id_consignment: string,
    lines: { id_variant: string, quantity: number }[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let consignmentResponse: Consignment | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        consignmentResponse = {
          id: id_consignment,
          id_company: id_company,
          id_contact: `contact${(Math.floor(Math.random() * 1000)).toString()}`,
          commission_type: 'percentage',
          commission_rate: 0,
          status: 'active',
          start_date: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
          end_date: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
          created_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
          updated_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`
        }

        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: consignmentResponse
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null 
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/consignments/${id_consignment}/return`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        lines: lines
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postConsignmentInvoice(
    id_company: string,
    id_consignment: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let invoiceResponse: Invoice | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoiceResponse = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: invoiceResponse
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null 
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/consignments/${id_consignment}/generate-invoice`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.post(url, null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postConsignmentClose(
    id_company: string,
    id_consignment: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: { status: 'closed' }
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/consignments/${id_consignment}/close`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.post(url, null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }
}
